'use client'

import { useAtom } from 'jotai'
import { useEffect } from 'react'

import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { useUserAccounts } from '@/service/account.hook'
import { isTradeVenueEnabled, useVenues } from '@/service/venue.hook'
import { exchangeAtom } from '@/state/storage'

/**
 * Headless — keeps `exchangeAtom` populated and valid for the handful of
 * remaining consumers that still read it directly (a few legacy
 * account-filtered atoms in state/account.ts, some not-yet-migrated chat
 * response cards) now that no widget's own trading logic depends on it —
 * see hooks/use-card-account.ts for the per-card replacement everything else
 * uses. Previously this lived inside account-selector.tsx's header dropdown,
 * so it only ran on the desktop trade header; mounting it globally here also
 * means it now runs on mobile/onboarding routes too.
 */
export function ActiveExchangeSync() {
  const { isLoggedIn } = useAuthGuard()
  // Gated on isLoggedIn — this component is mounted globally (including
  // /login and every other logged-out route), and an ungated query here
  // fired an authenticated fetchUserAccounts() call on every one of them.
  // With stale AUTH_STATE evidence still in the cookie, that request tripped
  // the axios interceptor's token refresh, which correctly got rejected (no
  // real session), which redirected to /login — the same page already being
  // rendered, reloading it and re-mounting this component to repeat the
  // cycle. A genuinely logged-out visit now never issues this request at all.
  const { data: accounts } = useUserAccounts({ enabled: isLoggedIn })
  // venuesData is only ever read below inside the isLoggedIn-gated effect —
  // same reasoning as useUserAccounts above.
  const { data: venuesData } = useVenues({ enabled: isLoggedIn })
  const [savedExchange, setSavedExchange] = useAtom(exchangeAtom)

  // Clear saved exchange on logout so stale demo data doesn't persist
  useEffect(() => {
    if (!isLoggedIn) {
      setSavedExchange(null)
    }
  }, [isLoggedIn, setSavedExchange])

  // Keep exchangeAtom in sync with the derived selection (handles stale data / first-load fallback).
  // Also steers away from an account whose venue the backend has pulled (e.g. Binance was
  // removed from prod's venue registry outright) — a saved selection on a dead venue would
  // otherwise persist forever, silently failing every instrument-list fetch keyed off it.
  // Only actually switches once venues have loaded (isTradeVenueEnabled defaults to true while
  // pending), and never strands the user with no selection if every account is on a dead venue.
  useEffect(() => {
    if (!isLoggedIn) return
    if (!accounts?.accounts.length) return
    const enabledAccounts = accounts.accounts.filter((a) =>
      isTradeVenueEnabled(venuesData?.venues, a.tradeVenue)
    )
    const candidates = enabledAccounts.length > 0 ? enabledAccounts : accounts.accounts
    const account =
      candidates.find((a) => a.accountId === savedExchange?.accountId) ?? candidates[0]

    if (
      !savedExchange ||
      account.accountId !== savedExchange.accountId ||
      account.tradeVenue !== savedExchange.tradeVenue ||
      account.status !== savedExchange.status ||
      account.name !== savedExchange.name ||
      account.updatedAt !== savedExchange.updatedAt
    ) {
      setSavedExchange(account)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts?.accounts, venuesData?.venues, isLoggedIn, savedExchange?.accountId])

  return null
}
